# THE CLIENT'S ROUTER — card 6ac022caa664bc34d57be8c1 (umbrella
# 6ac01b76a664bc34d57be442, owner 2026-10-02).
#
# It replaces @uirouter/react. The shells keep their own boot and screens; what
# changes is the route table, which now has the shape of a schema App row's
# manifest (framework/models/App.json; BaseApp/src/AppMain.coffee builds its
# routes the same way):
#
#     key            -> entry
#     '/dashboard?studentId'  -> {name: 'dashboard', component, requireLogin: true, ...}
#     'AgentView'             -> {name: 'Agent View', componentId: '…'}
#
# so built-in screens and an App row's Component screens can sit in ONE table
# and `route(name, params)` reaches either.
#
# THE KEY IS THE URL. Two forms, because two kinds of link exist:
#
#   '/play/:gameId/:studentId?nodeId&activityId'   el-feet's own form, the one
#       mailed, bookmarked and linked for years (#/play/<id>/<id>?nodeId=…). A
#       ':name' segment is a path param; what follows '?' are the query params
#       the route reads. Undeclared query keys are ignored, as ui-router did.
#   'AgentView'                                     BaseApp's form, '#AgentView?x=1'.
#       Every query key reaches the screen.
#
# The empty key '' is the sign-in screen: '', '#' and '#/' all open it.
#
# AN ENTRY says:
#   name          what route() is called with.
#   component     the React component the view renders.
#   requireLogin / requireParent / requireStudent (any flag the shell names)
#                 guard flags. The SHELL supplies the guard functions
#                 (createRouter's `guards`), and they run in the order the shell
#                 lists them. A guard answers nothing to let the route open, or
#                 {state, params} to go there instead.
#   redirectTo    (target) -> {state, params} | undefined — a guard of the
#                 route's own, run after the flagged ones.
#   params        {name: {value, array}} — params a route takes that are NOT in
#                 its URL (they live only as long as the page does).
#   dynamic       ['studentId'] — params whose change re-renders the screen
#                 WITHOUT remounting it or re-reading its resolves (the
#                 dashboard's swipe writes the child here; the teacher's tab bar
#                 writes its tab).
#   resolve       {token: (params, target) -> value | promise} — read BEFORE the
#                 screen mounts; each answer reaches the screen as a prop named
#                 by its token. A resolve may answer `redirect('login')` to send
#                 the transition elsewhere.
#
# IMPORT-FREE ON PURPOSE: a pin loads this file as it is and drives it with a
# stub window (tests/pins/support/elfeetEsModule).

# ---------------------------------------------------------------- the table --

export compileRoutes = (table) ->
  byName = {}
  list = []
  for own key, entry of table ? {}
    throw new Error "route '#{key}' has no name" unless entry?.name?
    throw new Error "two routes are named '#{entry.name}'" if byName[entry.name]?
    route = Object.assign {}, entry, {key, pattern: parsePattern key}
    byName[entry.name] = route
    list.push route
  {byName, list}

parsePattern = (key) ->
  [path, query] = splitOnce "#{key ? ''}", '?'
  queryParams = (q for q in (query ? '').split('&') when q)
  if path is '' or path[0] is '/'
    segments = (s for s in path.split('/') when s isnt '')
    pathParams = (s.slice(1) for s in segments when s[0] is ':')
    {style: 'path', segments, pathParams, queryParams}
  else
    {style: 'bare', segments: [path], pathParams: [], queryParams}

splitOnce = (text, sep) ->
  i = text.indexOf sep
  if i < 0 then [text, null] else [text.slice(0, i), text.slice(i + 1)]

decode = (text) ->
  try decodeURIComponent text.replace(/\+/g, '%20') catch then text

# '?a=1&b=2&b=3' -> {a: '1', b: ['2', '3']}
export parseQuery = (query) ->
  out = {}
  for part in "#{query ? ''}".split('&') when part
    [k, v] = splitOnce part, '='
    k = decode k
    v = if v? then decode(v) else ''
    if out[k]?
      out[k] = [].concat out[k], v
    else
      out[k] = v
  out

# Where a hash leads: {name, params}, or null when no route claims it.
export parseHash = (hash, routes) ->
  h = "#{hash ? ''}"
  h = h.slice(1) if h[0] is '#'
  [path, query] = splitOnce h, '?'
  query = parseQuery query
  if path is '' or path[0] is '/'
    segs = (decode s for s in path.split('/') when s isnt '')
    for route in routes.list when route.pattern.style is 'path'
      params = matchSegments route.pattern.segments, segs
      continue unless params?
      for q in route.pattern.queryParams when query[q]?
        params[q] = query[q]
      return {name: route.name, params}
    # '#/Key' also opens a bare-key route: the shells build their boot links as
    # '#/<name>' (deep_link.routeHash), and a hosted App row's screen is keyed
    # bare. Path routes are matched first, so this never shadows a built-in.
    if segs.length is 1
      for route in routes.list when route.pattern.style is 'bare' and route.pattern.segments[0] is segs[0]
        return {name: route.name, params: query}
  else
    for route in routes.list when route.pattern.style is 'bare' and route.pattern.segments[0] is path
      return {name: route.name, params: query}
  null

matchSegments = (pattern, segs) ->
  return null unless pattern.length is segs.length
  params = {}
  for p, i in pattern
    if p[0] is ':'
      params[p.slice(1)] = segs[i]
    else if p isnt segs[i]
      return null
  params

# The hash a route and its params open: '#/play/<g>/<s>?nodeId=…'. Params the
# URL does not carry are left out of it, not dropped from the route.
export buildHash = (name, params = {}, routes) ->
  route = routes.byName[name]
  throw new Error "No such route: #{name}" unless route?
  {style, segments, queryParams} = route.pattern
  pairs = []
  addPair = (k, v) ->
    return unless v? and v isnt ''
    for one in [].concat v when one? and typeof one isnt 'object'
      pairs.push "#{encodeURIComponent k}=#{encodeURIComponent one}"
  if style is 'path'
    segs = for s in segments
      if s[0] is ':'
        v = params?[s.slice(1)]
        throw new Error "route #{name} needs #{s}" unless v? and v isnt ''
        encodeURIComponent v
      else
        s
    addPair q, params?[q] for q in queryParams
    path = '/' + segs.join('/')
  else
    addPair k, v for own k, v of params ? {}
    path = segments[0]
  "##{path}#{if pairs.length then '?' + pairs.join('&') else ''}"

# --------------------------------------------------------------- the router --

# What a resolve or guard answers to send the transition elsewhere.
export redirect = (state, params = {}) -> {__redirect: true, state, params}

isRedirect = (value) -> value? and value.__redirect is true

MAX_REDIRECTS = 20

# A failed transition, as the onError hooks see it: what was being opened, and
# why it could not be. `retry` re-runs the same transition with fresh resolves.
class TransitionFailure
  constructor: (@name, @params, @error) ->

export createRouter = (table, options = {}) -> new HashRouter table, options

class HashRouter
  constructor: (table, options = {}) ->
    @routes = compileRoutes table
    @otherwise = options.otherwise ? null
    @guards = options.guards ? {}
    @win = options.window ? (if typeof window isnt 'undefined' then window else null)
    @current = null
    @transitions = 0
    @pendingHash = null
    @listeners = []
    @hooks = {start: [], success: [], error: []}

  # ---- what screens and hooks see

  # route(name, params, options) — the one navigation call. options:
  #   location: true (push a history entry, the default) | 'replace' | false
  #   reload:   true re-runs the route's resolves and remounts it even when
  #             nothing changed
  #   inherit:  false stops a re-entry of the current route from keeping the
  #             params the call does not name
  # 'name' may be '.', the route already open.
  go: (name, params = {}, options = {}) =>
    name = @current?.name if name is '.'
    route = @routes.byName[name]
    unless route?
      console.error "No such route: #{name}"
      return Promise.resolve null
    params = @paramsFor route, params ? {}, options
    if @current? and @current.name is name and not options.reload
      if sameParams @current.params, params
        # Nothing to open — but the address may still be wrong: an address no
        # route claims falls back to the screen already on view, and ui-router
        # rewrote the bar to that screen's own address. writeUrl leaves it alone
        # when it already matches.
        @writeUrl name, params, options
        return Promise.resolve(@current)
      if @onlyDynamicChanged route, @current.params, params
        @current = Object.assign {}, @current, {params}
        @writeUrl name, params, options
        @emit()
        @fire 'success', @current
        return Promise.resolve @current
    @transition route, params, options, 0

  href: (name, params = {}) =>
    name = @current?.name if name is '.'
    try buildHash(name, params, @routes) catch then null

  back: =>
    @win?.history?.back?()

  # Hooks, each answering its own unsubscribe.
  onStart: (fn) => @hook 'start', fn
  onSuccess: (fn) => @hook 'success', fn
  onError: (fn) => @hook 'error', fn

  subscribe: (fn) =>
    @listeners.push fn
    => @listeners = (l for l in @listeners when l isnt fn)

  # ---- the URL

  start: ->
    return this if @started
    @started = true
    @onHashChange = => @syncFromUrl()
    @win?.addEventListener? 'hashchange', @onHashChange
    @syncFromUrl()
    this

  stop: ->
    @win?.removeEventListener? 'hashchange', @onHashChange if @onHashChange?
    @started = false

  # The address bar changed under us — a typed hash, a link, the back button,
  # or a shell assigning window.location. Our own writes come back here too and
  # are recognised.
  syncFromUrl: =>
    hash = "#{@win?.location?.hash ? ''}"
    if @pendingHash? and hash is @pendingHash
      @pendingHash = null
      return Promise.resolve @current
    @pendingHash = null
    match = parseHash hash, @routes
    unless match?
      return Promise.resolve(null) unless @otherwise?
      return @go @otherwise, {}, {location: 'replace', inherit: false}
    @go match.name, match.params, {location: false, inherit: false}

  # ---- the transition

  paramsFor: (route, given, options) ->
    declared = route.pattern.pathParams.concat route.pattern.queryParams, Object.keys(route.params ? {})
    if route.pattern.style is 'bare'
      declared = declared.concat Object.keys(given)
    out = {}
    inheritFrom = if options.inherit isnt false and @current?.name is route.name then @current.params else {}
    for key in declared
      if given[key] isnt undefined
        out[key] = given[key]
      else if inheritFrom[key] isnt undefined
        out[key] = inheritFrom[key]
      else if route.params?[key]?.value isnt undefined
        out[key] = route.params[key].value
    # A param the URL carries is a STRING, however it was handed in — ui-router
    # typed them so, and the screens compare that way: the org application opens
    # the dashboard with {showIntro: true} and the dashboard asks
    # `showIntro == 'true'`. Handed through as a boolean, the intro dialog never
    # opened and every org, school and district setup stalled on it. A screen
    # sees the same value whether it arrived by route() or by a reload of the
    # address. Params outside the URL (route.params) keep what they were given.
    for key in route.pattern.pathParams.concat(route.pattern.queryParams) when out[key]?
      out[key] = toUrlString out[key]
    out

  onlyDynamicChanged: (route, before, after) ->
    dynamic = route.dynamic ? []
    return false unless dynamic.length
    keys = Object.keys(Object.assign {}, before, after)
    changed = (k for k in keys when not sameValue before[k], after[k])
    changed.length > 0 and changed.every (k) -> k in dynamic

  transition: (route, params, options, depth) ->
    id = ++@transitions
    target = {name: route.name, params}
    @fire 'start', target
    Promise.resolve()
      .then => @guard route, target
      .then (redirected) =>
        return redirected if redirected?
        @resolve(route, target).then (resolved) =>
          return resolved if isRedirect resolved
          return null unless id is @transitions   # superseded: the newer one speaks
          @commit route, params, resolved, options, id
      .then (outcome) =>
        return outcome unless isRedirect outcome
        if depth >= MAX_REDIRECTS
          throw new Error "Too many redirects opening #{route.name}"
        next = @routes.byName[outcome.state]
        throw new Error "No such route: #{outcome.state}" unless next?
        # A redirect REPLACES the address it was asked for, so the back button
        # does not land on a screen that only bounces again.
        nextOptions = Object.assign {}, options, {location: (if options.location is false then 'replace' else options.location ? 'replace'), inherit: false}
        @transition next, @paramsFor(next, outcome.params ? {}, nextOptions), nextOptions, depth + 1
      .catch (error) =>
        return null unless id is @transitions
        @fire 'error', new TransitionFailure(route.name, params, error)
        null

  guard: (route, target) ->
    for flag, fn of @guards when route[flag] and typeof fn is 'function'
      answer = fn target, route
      return redirect(answer.state, answer.params) if answer?.state?
    if typeof route.redirectTo is 'function'
      answer = route.redirectTo target, route
      return redirect(answer.state, answer.params) if answer?.state?
    null

  resolve: (route, target) ->
    tokens = Object.keys(route.resolve ? {})
    Promise.all(tokens.map (token) -> Promise.resolve().then -> route.resolve[token](target.params, target))
      .then (values) ->
        resolved = {}
        for token, i in tokens
          return values[i] if isRedirect values[i]
          resolved[token] = values[i]
        resolved

  commit: (route, params, resolved, options, id) ->
    @current = {name: route.name, params, route, resolved, key: id}
    @writeUrl route.name, params, options
    @emit()
    @fire 'success', @current
    @current

  writeUrl: (name, params, options) ->
    return if options.location is false
    return unless @win?.location?
    hash = buildHash name, params, @routes
    return if "#{@win.location.hash ? ''}" is hash
    if options.location is 'replace' and @win.history?.replaceState?
      url = "#{@win.location.pathname ? ''}#{@win.location.search ? ''}#{hash}"
      @win.history.replaceState @win.history.state, '', url
      # replaceState is silent, and the address bar has listeners besides this
      # router: the agent widget (client/widget/agent-widget-loader.js) tears
      # its frame down on a child's route and posts its context on hashchange.
      # ui-router assigned location.hash for every change, so they heard every
      # one; announce this one too. Our own listener knows it for ours.
      @announceHashChange hash
    else
      @pendingHash = hash
      @win.location.hash = hash

  announceHashChange: (hash) ->
    return unless @win.dispatchEvent?
    event = try
      new @win.HashChangeEvent 'hashchange'
    catch
      try new @win.Event('hashchange') catch then null
    return unless event?
    @pendingHash = hash
    try @win.dispatchEvent event catch then @pendingHash = null

  # ---- plumbing

  hook: (kind, fn) ->
    @hooks[kind].push fn
    => @hooks[kind] = (h for h in @hooks[kind] when h isnt fn)

  fire: (kind, payload) ->
    for fn in @hooks[kind].slice()
      try
        fn payload, this
      catch err
        console.error "Router #{kind} hook threw: #{err?.message or err}"
    return

  emit: ->
    for fn in @listeners.slice()
      fn @current
    return

toUrlString = (value) ->
  if Array.isArray(value) then (toUrlString v for v in value) else if typeof value is 'object' then value else "#{value}"

sameValue = (a, b) ->
  return true if a is b
  return false unless a? and b?
  if Array.isArray(a) or Array.isArray(b)
    return false unless Array.isArray(a) and Array.isArray(b) and a.length is b.length
    return a.every (v, i) -> sameValue v, b[i]
  "#{a}" is "#{b}" and typeof a isnt 'object' and typeof b isnt 'object'

sameParams = (a = {}, b = {}) ->
  keys = Object.keys(Object.assign {}, a, b)
  keys.every (k) -> sameValue a[k], b[k]
